import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ProfilePicture from './ProfilePicture';
import { getTenantSubdomain } from '../services/api';

const AdminSidebar = ({ 
  userProfileImage,
  userName,
  userSubtitle,
  activeMenuItem = "dashboard",
  onMenuItemClick,
  onSignOut,
  onProfileClick,
  alertCount = 0,
  userRole = 'ADMIN',
  showJiraTickets = false,
  showJiraComments = false,
  userInitials,
  isMobileOpen = false,
  onClose
}) => {
  const { t } = useTranslation('nav');
  const displayName = userName || t('adminConsoleDefault');
  const displaySubtitle = userSubtitle || t('adminSubtitleDefault');
  const tenantSubdomain = (getTenantSubdomain() || '').toLowerCase();
  const isRsTenant = tenantSubdomain === 'rs';
  // Same grouped layout for all tenants (Nokia, RS, …); only item visibility differs by tenant
  const useGroupedNav = userRole === 'ADMIN' || userRole === 'MANAGER';
  const allMenuItems = [
    { id: 'dashboard', icon: 'dashboard', section: 'main' },
    { id: 'imputations', icon: 'assignment', section: 'main' },
    { id: 'centres', icon: 'location_city', section: 'main' },
    { id: 'teams', icon: 'groups', section: 'main' },
    { id: 'my-teams', icon: 'groups', section: 'main' },
    { id: 'teams-planning', icon: 'calendar_month', section: 'main' },
    { id: 'users', icon: 'group', section: 'main' },
    { id: 'operations', icon: 'settings', section: 'main' },
    // { id: 'active-operations-json', icon: 'data_object', section: 'system' },
    { id: 'badge-configuration', icon: 'emoji_events', section: 'main' },
    { id: 'reward-management', icon: 'card_giftcard', section: 'main' },
    { id: 'performance-tracking', icon: 'analytics', section: 'main' },
    { id: 'jira-tickets', icon: 'confirmation_number', section: 'main' },
    { id: 'jira-comments', icon: 'comment', section: 'main' },
    { id: 'operation-logs', icon: 'history', section: 'system' },
  ];

  // Filter menu items based on user role
  const baseMenuItems = userRole === 'MANAGER'
    ? allMenuItems.filter(item => ['dashboard', 'imputations', 'my-teams', 'teams-planning', 'operations', 'badge-configuration', 'reward-management', 'performance-tracking', 'operation-logs', 'active-operations-json'].includes(item.id))
    : allMenuItems.filter(item => item.id !== 'my-teams');
  const menuItems = baseMenuItems.filter((item) => {
    if (item.id === 'jira-tickets' && !showJiraTickets) return false;
    if (item.id === 'jira-comments' && !showJiraComments) return false;
    // Teams planning is RS-only (Management group)
    if (item.id === 'teams-planning' && !isRsTenant) return false;
    return true;
  });

  const mainItems = menuItems.filter(item => item.section === 'main');
  const systemItems = menuItems.filter(item => item.section === 'system');
  const menuItemMap = new Map(menuItems.map((item) => [item.id, item]));
  const navGroups = [
    { sectionKey: 'management', itemIds: ['centres', 'teams', 'my-teams', 'teams-planning', 'users'] },
    { sectionKey: 'gamification', itemIds: ['badge-configuration', 'reward-management', 'performance-tracking'] },
    { sectionKey: 'productivity', itemIds: ['operations', 'imputations', 'operation-logs', 'active-operations-json'] },
    { sectionKey: 'triggers', itemIds: ['jira-tickets', 'jira-comments'] },
  ];
  const visibleNavGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.itemIds.map((id) => menuItemMap.get(id)).filter(Boolean),
    }))
    .filter((group) => group.items.length > 0);
  
  const SIDEBAR_LOCK_KEY = 'adminSidebarLocked';
  const itemRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLocked, setIsLocked] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_LOCK_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const isExpanded = isMobile || isHovered || isLocked;

  const defaultProfileImage = "https://lh3.googleusercontent.com/aida-public/AB6AXuBn46b1j429RzppC5YmhqSuXCegWUsXjZNJ4TPBPm4eWQFJK5D8g07ztPHhHJ9Rti8eTB-jezI0y48SBSicrReDCOlpn1s1Wsqb-5F45A3J39RVe_i7QQ8Rn7OFEO1VpV7Zc2OT_fiwK-7e2Cyuu6lHoApl1a5OM3eQcCi2Hto6pPHOfctfgoPrWU5Q67fP229iKoAhb7fAsHnCe7cx077Qa0fSPzL3tRC1d3tWW4zdwS1YkPdUuz2AerUQ7xFVJpVQssaWMblyP1k";

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleItemClick = (itemId) => {
    if (onMenuItemClick) {
      onMenuItemClick(itemId);
    }
    // Close mobile menu when item is clicked
    if (onClose) {
      onClose();
    }
  };

  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    }
  };

  const toggleLock = () => {
    setIsLocked((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_LOCK_KEY, String(next));
      } catch (_) {}
      return next;
    });
  };

  // Function to update indicator position
  const updateIndicatorPosition = useCallback(() => {
    const activeRef = itemRefs.current[activeMenuItem];
    if (activeRef) {
      const navElement = activeRef.closest('nav');
      if (navElement) {
        const navRect = navElement.getBoundingClientRect();
        const itemRect = activeRef.getBoundingClientRect();
        const top = itemRect.top - navRect.top + navElement.scrollTop;
        const height = itemRect.height;
        
        setIndicatorStyle({
          top: `${top}px`,
          height: `${height}px`,
          opacity: 1,
        });
      }
    }
  }, [activeMenuItem]);

  // Update indicator when active item or expand state changes (section labels shift layout)
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(updateIndicatorPosition, 0);
    return () => clearTimeout(timer);
  }, [activeMenuItem, isExpanded, useGroupedNav, updateIndicatorPosition]);

  // Set initial position on mount
  useEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition]);

  // Update position on scroll
  useEffect(() => {
    const navElement = document.querySelector('nav');
    if (navElement) {
      const handleScroll = () => {
        updateIndicatorPosition();
      };
      navElement.addEventListener('scroll', handleScroll);
      return () => navElement.removeEventListener('scroll', handleScroll);
    }
  }, [updateIndicatorPosition]);

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    }
    // Close mobile menu when profile is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={onClose}
            />
          </>
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isMobile && !isMobileOpen ? -288 : 0,
          width: isMobile ? 288 : (isExpanded ? 288 : 72)
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-surface border-r border-border flex-col fixed md:relative flex md:flex h-full shadow-sm dark:shadow-[4px_0_24px_rgba(0,0,0,0.25)] z-50 overflow-hidden shrink-0"
        style={{ minWidth: isMobile ? undefined : 72 }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
      <div 
        className={`border-b border-border flex items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${isExpanded ? 'p-6 gap-4' : 'p-3 justify-center'}`}
        onClick={handleProfileClick}
      >
        <div className="relative shrink-0">
          <ProfilePicture
            profilePictureUrl={userProfileImage}
            initials={userInitials || (displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'A')}
            size={isExpanded ? "md" : "sm"}
          />
          <div className="absolute bottom-0 right-0 size-3 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-800"></div>
        </div>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col min-w-0 overflow-hidden flex-1"
            >
              <h1 className="text-primary text-base font-bold leading-tight truncate">{displayName}</h1>
              <p className="text-text-secondary text-xs font-normal truncate">{displaySubtitle}</p>
            </motion.div>
          )}
        </AnimatePresence>
        {!isMobile && (
          <button
            type="button"
            title={isLocked ? t('unlockSidebar') : t('lockSidebar')}
            className="p-1.5 rounded-md text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary transition-colors shrink-0 ml-auto"
            onClick={(e) => {
              e.stopPropagation();
              toggleLock();
            }}
          >
            <span className={`material-symbols-outlined text-[20px] block ${isLocked ? 'text-primary' : ''}`}>
              {isLocked ? 'dock_to_right' : 'crop_square'}
            </span>
          </button>
        )}
      </div>
      <nav className={`flex-1 overflow-y-auto py-6 flex flex-col gap-2 relative ${isExpanded ? 'px-4' : 'px-2'}`}>
        {/* Sliding background indicator */}
        <motion.div
          className={`absolute bg-primary rounded-lg shadow-md shadow-blue-900/10 z-0 ${isExpanded ? 'left-4 right-4' : 'left-2 right-2'}`}
          initial={false}
          animate={{
            top: indicatorStyle.top,
            height: indicatorStyle.height,
            opacity: indicatorStyle.opacity,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            mass: 0.5,
          }}
        />

        {useGroupedNav ? (
          <>
            {mainItems
              .filter((item) => item.id === 'dashboard')
              .map((item, index) => (
                <motion.a
                  key={item.id}
                  ref={(el) => (itemRefs.current[item.id] = el)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-3 rounded-lg transition-all cursor-pointer relative z-10 ${
                    isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                  } ${
                    activeMenuItem === item.id
                      ? 'text-white'
                      : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary'
                  }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleItemClick(item.id);
                  }}
                >
                  <motion.span
                    className="material-symbols-outlined text-[20px] shrink-0"
                    animate={activeMenuItem === item.id ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    {item.icon}
                  </motion.span>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-sm font-medium whitespace-nowrap overflow-hidden"
                      >
                        {t(`menu.${item.id}`)}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.a>
              ))}

            <div className="my-2 border-t border-border relative z-10"></div>

            {visibleNavGroups.map((group, groupIndex) => (
              <div key={group.sectionKey} className="relative z-10">
                {isExpanded && (
                  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-secondary/80">
                    {t(`section.${group.sectionKey}`)}
                  </p>
                )}
                {group.items.map((item, itemIndex) => (
                  <motion.a
                    key={item.id}
                    ref={(el) => (itemRefs.current[item.id] = el)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: (groupIndex * 4 + itemIndex) * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center gap-3 rounded-lg transition-all cursor-pointer relative z-10 ${
                      isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                    } ${
                      activeMenuItem === item.id
                        ? 'text-white'
                        : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary'
                    }`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleItemClick(item.id);
                    }}
                  >
                    <motion.span
                      className="material-symbols-outlined text-[20px] shrink-0"
                      animate={activeMenuItem === item.id ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    >
                      {item.icon}
                    </motion.span>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-sm font-medium whitespace-nowrap overflow-hidden"
                        >
                          {t(`menu.${item.id}`)}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {isExpanded && item.badge && item.badge > 0 && (
                      <motion.span
                        className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 15 }}
                      >
                        {item.badge}
                      </motion.span>
                    )}
                  </motion.a>
                ))}
                {groupIndex < visibleNavGroups.length - 1 && (
                  <div className="my-2 border-t border-border relative z-10"></div>
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            {mainItems.map((item, index) => (
              <motion.a
                key={item.id}
                ref={(el) => (itemRefs.current[item.id] = el)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-3 rounded-lg transition-all cursor-pointer relative z-10 ${
                  isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${
                  activeMenuItem === item.id
                    ? 'text-white'
                    : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary'
                }`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleItemClick(item.id);
                }}
              >
                <motion.span
                  className="material-symbols-outlined text-[20px] shrink-0"
                  animate={activeMenuItem === item.id ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  {item.icon}
                </motion.span>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-sm font-medium whitespace-nowrap overflow-hidden"
                    >
                      {t(`menu.${item.id}`)}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.a>
            ))}
            <div className="my-2 border-t border-border relative z-10"></div>
            {systemItems.map((item, index) => (
              <motion.a
                key={item.id}
                ref={(el) => (itemRefs.current[item.id] = el)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: (mainItems.length + index) * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-3 rounded-lg transition-all cursor-pointer relative z-10 ${
                  isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${
                  activeMenuItem === item.id
                    ? 'text-white'
                    : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-600/50 hover:text-primary'
                }`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleItemClick(item.id);
                }}
              >
                <motion.span
                  className="material-symbols-outlined text-[20px] shrink-0"
                  animate={activeMenuItem === item.id ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  {item.icon}
                </motion.span>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-sm font-medium whitespace-nowrap overflow-hidden"
                    >
                      {t(`menu.${item.id}`)}
                    </motion.span>
                  )}
                </AnimatePresence>
                {isExpanded && item.badge && item.badge > 0 && (
                  <motion.span
                    className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 15 }}
                  >
                    {item.badge}
                  </motion.span>
                )}
              </motion.a>
            ))}
          </>
        )}
      </nav>
      <div className={`border-t border-border ${isExpanded ? 'p-4' : 'p-3'}`}>
        <button 
          className={`flex items-center rounded-lg text-text-secondary hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/40 dark:hover:text-red-300 transition-colors w-full ${
            isExpanded ? 'gap-3 px-3 py-2' : 'justify-center p-2'
          }`}
          onClick={handleSignOut}
        >
          <span className="material-symbols-outlined text-[20px] shrink-0">logout</span>
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-sm font-medium whitespace-nowrap overflow-hidden"
              >
                {t('signOut')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
    </>
  );
};

export default AdminSidebar;
